import { VectorFeatureControl } from "../controls/VectorFeatureControl";
import { RevisionControl } from "../controls/RevisionControl";
import {
  buildRevisionParams,
  fetchRevisions,
} from "../services/RevisionService";
import { getWFSLayer } from "../layers/WFSLayer";
import { getHoverLayer } from "../layers/HoverLayer";
import { withCurvatureReconstructionToggle } from "../layers/addfeaturehandlers/curvatureReconstructionToggle";
import {
  getLayersFromURL,
  getRevisionsFromURL,
  registerLayerControl,
  registerRevisionControl,
} from "../state/URLStateManager";
import { assertSlug } from "./layerSlug.mjs";

export function addWFSLayerAndControl({
  layerName,
  slug,
  url,
  role,
  style,
  azureLoginControl,
  featureInfoControl,
  div,
  map,
  getInteractions,
  featureInfoHandlers,
  hoverStyleFunctions,
  addFeatureHandlers = [],
}) {
  // `slug` is the layer's URL identity and is deliberately NOT derived from
  // layerName — see config/layerSlug.mjs for why.
  assertSlug(slug, layerName);

  const control = new VectorFeatureControl({
    layer_name: layerName,
    target: div,
  });

  // Restore layer visibility from URL.
  if (getLayersFromURL().includes(slug)) {
    control.setToggle(true);
  }
  registerLayerControl(slug, control);

  const { addFeatureHandlers: resolvedAddFeatureHandlers, bindLayer } =
    withCurvatureReconstructionToggle(addFeatureHandlers, control.headerEl);

  // Revision state shared between the control and the layer URL builder
  let currentRevisionParams = [];
  let hasAppliedUrlRevisions = false;

  const revisionControl = new RevisionControl({
    headerEl: control.headerEl,
    bodyEl: control.bodyEl,
  });
  registerRevisionControl(slug, revisionControl);

  // Fetch revisions once the auth token is available (or immediately if already set).
  // `userRefresh` is true only for the "↺ Latest" button click — see the emit
  // branch below for why system-driven fetches must NOT be treated as a selection.
  function tryFetchRevisions(userRefresh = false) {
    const token = azureLoginControl.get("idToken");
    if (!token) return;
    fetchRevisions(url, token)
      .then((layerRevisions) => {
        let revisionsToUse = layerRevisions;
        let restoredFromUrl = false;

        // Apply URL revision overrides on first load only
        if (!hasAppliedUrlRevisions) {
          hasAppliedUrlRevisions = true;
          const urlRevisions = getRevisionsFromURL();
          const slugRevisions = urlRevisions.get(slug) || [];
          if (slugRevisions.length > 0) {
            restoredFromUrl = true;
            revisionsToUse = layerRevisions.map((lr) => {
              const match = slugRevisions.find((u) => u.layerId === lr.layerId);
              // Overlay the URL value onto `revision` but keep the true
              // server-latest in `latest` so RevisionControl can still derive
              // the "latest" label for restored non-latest values.
              return match
                ? { ...lr, revision: match.revision, latest: lr.revision }
                : { ...lr, latest: lr.revision };
            });
            // Check if any revision actually differs from the latest
            const hasOverride = revisionsToUse.some(
              (r, i) => r.revision !== layerRevisions[i].revision,
            );
            if (hasOverride) {
              revisionControl.expand();
            }
          }
        }

        const newRevisionParams = buildRevisionParams(revisionsToUse);
        const changed =
          newRevisionParams.length !== currentRevisionParams.length ||
          newRevisionParams.some((p, i) => p !== currentRevisionParams[i]);

        // On URL restore the passed values are authoritative — keep them and
        // only derive labels (non-destructive). Otherwise apply preferred source.
        revisionControl.setRevisions(revisionsToUse, restoredFromUrl);
        currentRevisionParams = newRevisionParams;

        if (restoredFromUrl) {
          // Restored from URL: publish through the RevisionControl (its
          // selectedRevisions handler below forwards to the layer control and
          // refreshes the data). This also lets URLStateManager re-serialize
          // the revisions: the initial pushState runs before this async fetch
          // resolves and would otherwise drop the revision state from the URL.
          // Emit only { layerId, revision } (the URL values) so the re-serialize
          // is idempotent and the internal `latest` field doesn't leak.
          revisionControl.set(
            "selectedRevisions",
            revisionsToUse.map(({ layerId, revision }) => ({
              layerId,
              revision,
            })),
          );
        } else if (userRefresh) {
          // User clicked "↺ Latest": this refetch IS an explicit selection, so
          // publish it through the RevisionControl. That both forwards to the
          // layer (data refresh, via the selectedRevisions listener below) AND
          // triggers URLStateManager to re-serialize the new revisions — without
          // this, the rows update on screen but the URL keeps the pre-click
          // values, so a reload/new-tab silently reverts the selection.
          // Emit the control's *applied* values (not the raw fetch) so the URL
          // stays consistent with what's displayed. The system-driven fetches
          // (initial load, token change) fall through to the `changed` branch
          // and deliberately do NOT emit here: emitting would freeze an
          // untouched layer's latest into the URL and stop it tracking latest.
          revisionControl.emitSelected();
        } else if (changed) {
          control.set("selectedRevisions", revisionsToUse);
        }
      })
      .catch((err) => {
        revisionControl.setError(err);
      });
  }

  // Try immediately; also re-fetch whenever the token changes (login/logout)
  tryFetchRevisions();
  azureLoginControl.on("propertychange", function (e) {
    if (e.key === "idToken") {
      tryFetchRevisions();
    }
  });

  // Re-fetch when the user clicks the "↺ Latest" button
  revisionControl.on("propertychange", function (e) {
    if (e.key === "refreshRequested") {
      revisionControl.setLoading();
      // user-initiated → persist the refetched selection to the URL
      tryFetchRevisions(true);
    }
  });

  // Keep currentRevisionParams in sync when the user adjusts a revision value
  revisionControl.on("propertychange", function (e) {
    if (e.key === "selectedRevisions") {
      currentRevisionParams = buildRevisionParams(
        revisionControl.get("selectedRevisions"),
      );
      // Forward to VectorFeatureControl so WFSLayer can react
      control.set(
        "selectedRevisions",
        revisionControl.get("selectedRevisions"),
      );
    }
  });

  const wfsUrl = url + "/wfs";
  const layer = getWFSLayer(
    azureLoginControl,
    control,
    wfsUrl,
    role,
    style,
    resolvedAddFeatureHandlers,
    () => currentRevisionParams,
  );
  bindLayer(layer);

  map.addControl(control);
  map.addControl(revisionControl);
  map.addLayer(layer);
  hoverStyleFunctions.forEach((hoverStyleFunction) => {
    const hoverLayer = getHoverLayer(hoverStyleFunction);
    map.addLayer(hoverLayer);
    featureInfoControl.addHoverLayerForLayer(hoverLayer, layer);
  });
  featureInfoHandlers.forEach((handler) =>
    featureInfoControl.addHandlerForLayer(handler, layer),
  );

  getInteractions(layer, control).forEach((interaction) =>
    map.addInteraction(interaction),
  );
  map.on("moveend", function () {
    control.set("zoom", map.getView().getZoom());
  });
  azureLoginControl.on("propertychange", function () {
    layer.getSource().refresh();
  });
}
